import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, User, X, Home, LayoutGrid } from "lucide-react";
import { useCart } from "../context/CartContext";
import SearchOverlay from "./SearchOverlay";

export const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Flowers", to: "/category/flower-strains" },
  { label: "Pre-Rolls", to: "/category/pre-rolls" },
  { label: "Edibles", to: "/category/edibles" },
  { label: "Vapes", to: "/category/cartridges" },
  { label: "Concentrates", to: "/category/concentrates" },
  { label: "Deals", to: "/category/special-deals" },
  { label: "Delivery", to: "/delivery" },
  { label: "About", to: "/about" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isProductPage = location.pathname.startsWith("/product/") || location.pathname.startsWith("/checkout");

  const accountClick = () => navigate("/checkout", { state: { openPage: "account" } });

  return (
    <>
      <div className="relative overflow-hidden bg-brand-lime py-2.5" data-testid="promo-top-banner">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 animate-promo-shine bg-gradient-to-r from-transparent via-white/50 to-transparent" aria-hidden="true" />
        <p className="relative flex animate-fade-up flex-wrap items-center justify-center gap-x-1.5 gap-y-1 px-3 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-brand-ink sm:text-xs sm:tracking-[0.16em]">
          <span>
            Enjoy a <span className="text-brand-red">10% discount</span> on your first purchase!
          </span>
          <span className="inline-flex flex-wrap items-center justify-center gap-x-1.5">
            Use code
            <span className="inline-block animate-code-pop rounded-full bg-brand-ink px-2.5 py-0.5 tracking-[0.18em] text-brand-lime sm:px-3 sm:py-1">
              FirstPurchase
            </span>
            at checkout <span className="text-brand-ink/50">· 21+</span>
          </span>
        </p>
      </div>
      <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white/80 backdrop-blur-xl" data-testid="site-header">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
          <button
            className="rounded-md p-2 transition-colors hover:bg-neutral-100 lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <img src="/logo.webp" alt="Farmacy District DC" className="h-9 w-auto sm:h-10" />
          </Link>
          <nav className="ml-6 hidden items-center gap-5 lg:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold tracking-wide transition-colors hover:text-brand-red ${
                    isActive ? "text-brand-red" : "text-neutral-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <button
              className="rounded-full p-2 transition-colors hover:bg-neutral-100"
              onClick={() => setSearchOpen(true)}
              aria-label="Search products"
              data-testid="search-button"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              className="rounded-full p-2 transition-colors hover:bg-neutral-100"
              onClick={accountClick}
              aria-label="Account"
              data-testid="account-button"
            >
              <User className="h-5 w-5" />
            </button>
            <button
              className="relative rounded-full p-2 transition-colors hover:bg-neutral-100"
              onClick={() => setOpen(true)}
              aria-label="Open cart"
              data-testid="cart-button"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span
                  className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-red text-[10px] font-bold text-white"
                  data-testid="cart-count-badge"
                >
                  {count}
                </span>
              )}
            </button>
            <Link
              to="/shop"
              data-testid="header-shop-now-cta"
              className="ml-1 hidden rounded-full bg-brand-lime px-5 py-2.5 text-[13px] font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover md:block"
            >
              SHOP NOW
            </Link>
          </div>
        </div>

      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-brand-ink/60" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-[85%] max-w-sm flex-col bg-white shadow-[0_16px_48px_rgba(0,0,0,0.2)]">
            <div className="flex items-center justify-between border-b border-neutral-200 p-4">
              <img src="/logo.webp" alt="Farmacy District DC" className="h-9 w-auto" />
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="rounded-full p-2 hover:bg-neutral-100"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col overflow-y-auto p-4" aria-label="Mobile">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className="border-b border-neutral-100 py-4 font-display text-lg font-bold tracking-tight text-brand-ink transition-colors hover:text-brand-red"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/how-to-order"
                onClick={() => setMenuOpen(false)}
                className="border-b border-neutral-100 py-4 font-display text-lg font-bold tracking-tight text-brand-ink"
                data-testid="mobile-nav-how-to-order"
              >
                How to Order
              </Link>
              <Link
                to="/faq"
                onClick={() => setMenuOpen(false)}
                className="py-4 font-display text-lg font-bold tracking-tight text-brand-ink"
                data-testid="mobile-nav-faq"
              >
                FAQ
              </Link>
            </nav>
            <div className="mt-auto p-4">
              <Link
                to="/shop"
                onClick={() => setMenuOpen(false)}
                className="block rounded-full bg-brand-lime py-3.5 text-center text-sm font-bold tracking-wide text-brand-ink"
                data-testid="mobile-menu-shop-cta"
              >
                SHOP NOW
              </Link>
              <p className="mt-4 text-center text-xs text-neutral-500">(202) 913-4523 · Adults 21+</p>
            </div>
          </div>
        </div>
      )}

      {!isProductPage && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-4 border-t border-neutral-200 bg-white/90 backdrop-blur-xl md:hidden"
          aria-label="Quick actions"
          data-testid="mobile-bottom-bar"
        >
          {[
            { icon: Home, label: "Home", action: () => navigate("/"), id: "home" },
            { icon: LayoutGrid, label: "Shop", action: () => navigate("/shop"), id: "shop" },
            { icon: Search, label: "Search", action: () => setSearchOpen(true), id: "search" },
            { icon: ShoppingBag, label: "Cart", action: () => setOpen(true), id: "cart" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={item.action}
              data-testid={`bottom-bar-${item.id}`}
              className="relative flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold text-neutral-600 transition-colors hover:text-brand-ink"
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              {item.id === "cart" && count > 0 && (
                <span className="absolute right-[22%] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-red text-[9px] font-bold text-white">
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
